import {
	CleanseAddress as CleanseAddressModule,
	Data,
	Modal,
	UI,
	Utils,
} from 'common';
import every from 'lodash/every';
import isString from 'lodash/isString';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';

const { States } = Data;
const {
	WrappedInputTextWithLabel,
	WrappedDropdownWithLabel,
	WrappedButton,
	Spinner,
} = UI;
const { Session, Util, Logger, Validation, withLayoutAndModalContexts } = Utils;
const { ModalButtonActionType } = Modal;
const { CleanseAddress, CleanseAddressStatus } = CleanseAddressModule;

class ShipFromAddress extends Component {
	constructor() {
		super();

		this.state = {
			licenseInformation: {
				BusinessName: '',
				PStreet: '',
				PCity: '',
				PState: '',
				PZipCode: '',
				PAddOn: '',
			},
			isLoading: false,
			loadingMessage: '',
			clean: true,
			triggerCleanseAddress: false,
			validationErrors: {
				addressError: false,
				cityError: false,
				nameError: false,
				stateError: false,
				zipError: false,
			},
		};

		Session.setFormStateDirty(false);
	}

	componentDidMount() {
		this.getLicenseInformation();
	}

	/**
	 * Validate zip field. Error message refers to:
	 * https://stamps.atlassian.net/wiki/spaces/PRODREQ/pages/3122154/MyAccount+-+Contact+Info
	 * @param {string} zip
	 * @return {boolean|string} `fasle` means zip code is valid, otherwise, an error message.
	 */
	validateZip = zip => {
		if (zip === '') {
			return 'Zip code is required.';
		} else {
			return Validation.isValidZip(zip) ? false : 'Invalid ZIP Code.';
		}
	};

	/**
	 * Validate the entire form or a specific field.
	 * @param {string|null} name Pass fieldname to validate the field; otherwise, null to validate the entire form.
	 * @returns {boolean}
	 */
	isValid = name => {
		const { licenseInformation, validationErrors } = this.state;

		const newValidationErrors = {
			// it's invalid when value is an empty string
			nameError:
				name === 'BusinessName' || !name
					? licenseInformation.BusinessName === ''
					: validationErrors.nameError,
			addressError:
				name === 'PStreet' || !name
					? licenseInformation.PStreet === ''
					: validationErrors.addressError,
			cityError:
				name === 'PCity' || !name
					? licenseInformation.PCity === ''
					: validationErrors.cityError,

			// it's invalid when validation check doesn't pass
			zipError:
				name === 'zip' || !name
					? this.validateZip(
							Util.formatZip(
								licenseInformation.PZipCode + licenseInformation.PAddOn
							)
						)
					: validationErrors.zipError,
		};

		this.setState({
			validationErrors: newValidationErrors,
		});

		return every(Object.values(newValidationErrors), val => val === false);
	};

	handleInputChange = e => {
		const { name, value } = e.target;

		this.setState(({ licenseInformation }) => {
			const [pZipCode, pAddOn] =
				name === 'zip'
					? Util.formatZip(value).split('-')
					: [licenseInformation.PZipCode, licenseInformation.PAddOn];

			return {
				clean: false,
				licenseInformation: {
					...licenseInformation,
					BusinessName:
						name === 'BusinessName' ? value : licenseInformation.BusinessName,
					PStreet: name === 'PStreet' ? value : licenseInformation.PStreet,
					PCity: name === 'PCity' ? value : licenseInformation.PCity,
					PState: name === 'PState' ? value : licenseInformation.PState,
					PZipCode: pZipCode || '',
					PAddOn: pAddOn || '',
				},
			};
		});

		Session.setFormStateDirty(true);
	};

	// validate field on blur
	handleValidationOnBlur = e => {
		const { name } = e.target;

		this.isValid(name);
	};

	getLicenseInformation = async () => {
		const {
			enterpriseLocation,
			modalContext: { showError },
		} = this.props;
		const { customerId } = Session.getAccountInfo();
		const accountCustomerId = enterpriseLocation
			? enterpriseLocation.CustId
			: customerId;

		this.setState({
			isLoading: true,
			loadingMessage: 'Loading Ship From Address...',
		});

		const response =
			await AccountCommunication.getLicenseInformation(accountCustomerId);

		this.setState({
			isLoading: false,
			loadingMessage: '',
		});

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				licenseInformation: response.data,
			});
		} else {
			showError({
				response,
			});
		}
	};

	updateShipFromAddress = async data => {
		const {
			enterpriseLocation,
			modalContext: { showMessage, showError },
		} = this.props;
		const { customerId } = Session.getAccountInfo();
		const accountCustomerId = enterpriseLocation
			? enterpriseLocation.CustId
			: customerId;

		this.setState({
			isLoading: true,
			loadingMessage: 'Updating Ship From Address...',
		});

		const response = await AccountCommunication.updateLicenseInformation(
			data,
			accountCustomerId
		);

		this.setState({
			isLoading: false,
			loadingMessage: '',
		});

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				clean: true,
			});

			Session.setFormStateDirty(false);

			showMessage({
				data: {
					title: 'Address Updated',
					description: 'Your Ship From address has been successfully updated.',
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Ok',
				},
			});

			Logger.logToServer({
				category: 'Save Changes',
				component: 'shipfromaddress',
			});
		} else {
			this.setState({
				clean: false,
			});

			showError({
				response,
			});
		}
	};

	onSaveButtonClick = () => {
		const isValid = this.isValid();

		Logger.saveActionData('Save Ship From Address Button Clicked');

		// set request object and trigger address cleanse when form is valid
		if (isValid) {
			this.setState({
				triggerCleanseAddress: true,
			});
		}
	};

	onKeyPressEnter = e => {
		const {
			key,
			target: { value },
		} = e;

		if (key === 'Enter' && value !== '') {
			e.preventDefault();

			this.onSaveButtonClick();
		}
	};

	cleanseAddressCallback = cleanseAddressResponse => {
		const {
			modalContext: { showError },
		} = this.props;
		const { licenseInformation } = this.state;
		const { cleanseStatus, address = {}, isPoBox } = cleanseAddressResponse;
		const [pZipCode, pAddOn] = isString(address.postalCode)
			? address.postalCode.split('-')
			: [];
		let updatedLicenseInformation = {};

		switch (cleanseStatus) {
			case CleanseAddressStatus.Cleansed:
				updatedLicenseInformation = {
					...licenseInformation,
					BusinessName: address.name || '',
					PStreet: address.addressLine1,
					PCity: address.city,
					PState: address.stateProvince,
					PZipCode: pZipCode || '',
					PAddOn: pAddOn || '',
				};

				this.setState({
					licenseInformation: updatedLicenseInformation,
					clean: cleanseStatus === CleanseAddressStatus.Cleansed && !isPoBox,
					triggerCleanseAddress: false,
				});

				if (isPoBox) {
					showError({
						data: {
							title: 'Invalid Address',
							description: 'Your Ship From address cannot be a P.O. Box.',
							primaryButtonType: ModalButtonActionType.Ok,
							primaryButtonText: 'Close',
							handleType: 'custom',
						},
					});
				} else {
					this.updateShipFromAddress(updatedLicenseInformation);
				}
				break;

			case CleanseAddressStatus.InvalidCandidateAddress:
				showError({
					data: {
						title: 'Invalid Address',
						description: `The selected address does not meet return delivery requirements. Please choose a different address.`,
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'Close',
						handleType: 'custom',
					},
				});

				this.setState({
					clean: false,
					triggerCleanseAddress: false,
				});
				break;
			case CleanseAddressStatus.NotFound:
			case CleanseAddressStatus.MalformedAddress:
				showError({
					data: {
						title: 'Invalid Address',
						description: `We couldn't find an exact match for the city, state, and ZIP code.`,
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'Close',
						handleType: 'custom',
					},
				});

				this.setState({
					clean: false,
					triggerCleanseAddress: false,
				});
				break;

			default:
				this.setState({
					clean: false,
					triggerCleanseAddress: false,
				});
		}
	};

	render() {
		const {
			layoutContext: { isMobile, isPhoneLayout },
		} = this.props;
		const {
			licenseInformation,
			triggerCleanseAddress,
			validationErrors,
			clean,
			isLoading,
			loadingMessage,
		} = this.state;
		const { IsInWinClient } = Session.getSettings();
		const mobileClassName = isPhoneLayout === true ? 'mobile-wrapper' : '';

		return (
			<div className="account-ship-from-address">
				<div className="account-title">Ship From Address</div>
				<p className={`header-text ${mobileClassName}`}>
					{IsInWinClient ? (
						<span>
							This is the name and address registered with your letter and
							package carriers. In the Stamps.com Windows software, this address
							is used to calculate your rates.
							<br />
							<br />
							Anytime you change names or move to a new physical location, you
							should update your Ship From Address. PO Boxes are not valid Ship
							From addresses.
						</span>
					) : (
						<span>
							This is the name and address registered with your letter and
							package carriers.
							<br />
							Anytime you change names or move to a new physical location, you
							should update your Ship From Address. PO Boxes are not valid Ship
							From addresses.
						</span>
					)}
				</p>
				<div className="form-container main-contact">
					<WrappedInputTextWithLabel
						showErrorBelow={isMobile}
						isInputInvalid={validationErrors.nameError}
						errorMessage="Name is required."
						className={`inputtext ${mobileClassName}`}
						label="Name:"
						uiElementConfig={{
							type: 'text',
							name: 'BusinessName',
							value: licenseInformation.BusinessName,
							placeholder: 'Name',
							maxLength: 50,
							required: true,
							dataqa: 'shipfromname',
							onChange: this.handleInputChange,
							onBlur: this.handleValidationOnBlur,
							onKeyPress: this.onKeyPressEnter,
						}}
					/>
					<p>
						Enter your company name here. If you don’t have one, use your full
						name.
					</p>
					<WrappedInputTextWithLabel
						showErrorBelow={isMobile}
						isInputInvalid={validationErrors.addressError}
						errorMessage="Address is required."
						className={`inputtext ${mobileClassName}`}
						label="Address:"
						uiElementConfig={{
							type: 'text',
							name: 'PStreet',
							value: licenseInformation.PStreet,
							placeholder: 'Address',
							maxLength: 50,
							required: true,
							dataqa: 'shipfromaddress',
							onChange: this.handleInputChange,
							onBlur: this.handleValidationOnBlur,
							onKeyPress: this.onKeyPressEnter,
						}}
					/>
					<WrappedInputTextWithLabel
						showErrorBelow={isMobile}
						isInputInvalid={validationErrors.cityError}
						errorMessage="City is required."
						className={`inputtext ${mobileClassName}`}
						label="City:"
						uiElementConfig={{
							type: 'text',
							name: 'PCity',
							value: licenseInformation.PCity,
							placeholder: 'City',
							maxLength: 30,
							required: true,
							dataqa: 'shipfromcity',
							onChange: this.handleInputChange,
							onBlur: this.handleValidationOnBlur,
							onKeyPress: this.onKeyPressEnter,
						}}
					/>
					<WrappedDropdownWithLabel
						className={`dropdown ${mobileClassName}`}
						label="State:"
						uiElementConfig={{
							name: 'PState',
							value: licenseInformation.PState,
							options: States,
							optionLabel: 'name',
							optionValue: 'abbreviation',
							dataqa: 'shipfromstate',
							onChange: this.handleInputChange,
						}}
					/>
					<WrappedInputTextWithLabel
						showErrorBelow={isMobile}
						isInputInvalid={validationErrors.zipError !== false}
						errorMessage={validationErrors.zipError}
						className={`inputtext ${mobileClassName}`}
						label="Zip Code:"
						uiElementConfig={{
							type: 'text',
							name: 'zip',
							value: Util.formatZip(
								licenseInformation.PZipCode + licenseInformation.PAddOn
							),
							placeholder: 'Zip Code',
							maxLength: 10,
							required: true,
							dataqa: 'shipfromzip',
							onChange: this.handleInputChange,
							onBlur: this.handleValidationOnBlur,
							onKeyPress: this.onKeyPressEnter,
						}}
					/>
				</div>

				<WrappedButton
					className={`save-button ${mobileClassName}`}
					label="Save"
					disabled={clean || isLoading}
					onClick={this.onSaveButtonClick}
					onKeyPress={this.onKeyPressEnter}
					dataqa="ship-from-address-save"
				/>
				{triggerCleanseAddress && (
					<CleanseAddress
						fullAddress={{
							name: licenseInformation.BusinessName,
							addressLine1: licenseInformation.PStreet,
							city: licenseInformation.PCity,
							postalCode: Util.formatZip(
								licenseInformation.PZipCode + licenseInformation.PAddOn
							),
							stateProvince: licenseInformation.PState,
							countryCode: 'US',
						}}
						callback={this.cleanseAddressCallback}
					/>
				)}
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(ShipFromAddress);
